import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Activity } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * StatusIndicator — small icon button in the topbar that:
 * 1. Shows the current system health via a colored dot
 * 2. Navigates to /status when clicked
 *
 * Lives next to the ThemeToggle in the topbar. No auth — /status is
 * a public page. Polls /api/v1/status every 60s (matches the
 * server-side cache TTL) so the dot stays accurate without hammering
 * the endpoint.
 *
 * Dot colors:
 *   green  — all systems operational (pulses)
 *   amber  — at least one system degraded
 *   red    — API is down
 *   gray   — haven't checked yet, or endpoint unreachable
 */

const REFRESH_INTERVAL_MS = 60_000;

export function StatusIndicator() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('unknown'); // 'operational' | 'degraded' | 'outage' | 'unknown'
  const aliveRef = useRef(true);

  const check = useCallback(async () => {
    try {
      const r = await fetch('/api/v1/status');
      if (!r.ok) {
        if (aliveRef.current) setStatus('unknown');
        return;
      }
      const data = await r.json();
      if (!aliveRef.current) return;
      setStatus(data.status || 'unknown');
    } catch {
      if (aliveRef.current) setStatus('unknown');
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    check();
    const id = setInterval(check, REFRESH_INTERVAL_MS);
    // Re-check immediately on route change so the user sees fresh state
    // if they navigated back to a page from the status screen.
    return () => {
      aliveRef.current = false;
      clearInterval(id);
    };
  }, [check, location.pathname]);

  const dotClass = {
    operational: 'bg-emerald-500',
    degraded:    'bg-amber-500',
    outage:      'bg-red-500',
    unknown:     'bg-fg-subtle',
  }[status];

  const tooltip = {
    operational: 'All systems operational — view status',
    degraded:    'Some systems are degraded — view status',
    outage:      'Major outage — view status',
    unknown:     'System status — view status',
  }[status];

  // Active state when the user is already on the status page
  const isActive = location.pathname === '/status';

  return (
    <button
      type="button"
      onClick={() => navigate('/status')}
      title={tooltip}
      aria-label={tooltip}
      className={cn(
        'relative inline-flex items-center justify-center h-8 w-8 rounded-md transition-colors',
        isActive
          ? 'bg-surface-2 text-fg'
          : 'text-fg-muted hover:text-fg hover:bg-surface-2',
      )}
    >
      <Activity className="h-4 w-4" />
      {/* Status dot — top-right corner */}
      <span
        className={cn(
          'absolute top-1 right-1 h-1.5 w-1.5 rounded-full ring-2 ring-bg',
          dotClass,
          status === 'operational' && 'animate-pulse',
        )}
        aria-hidden="true"
      />
    </button>
  );
}
